const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001/api/v1';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Function to refresh access token
const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refresh_token');

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken })
    });

    if (!response.ok) {
      // If refresh fails, clear tokens and redirect to login
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login'; // Redirect to login page
      throw new Error('Token refresh failed');
    }

    const data = await response.json();

    // Update both tokens in localStorage
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);

    return data.access_token;
  } catch (error) {
    console.error('Error refreshing token:', error);
    throw error;
  }
};

// Wrapper function for API requests with automatic token refresh
const apiRequest = async (endpoint, options = {}) => {
  // Check if user has access token
  const token = localStorage.getItem('access_token');

  // If no token exists, redirect to login immediately
  if (!token) {
    console.warn('No access token found, redirecting to login');
    window.location.href = '/login';
    throw new Error('Authentication required');
  }

  let response = await fetch(endpoint, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers
    }
  });

  // If the request fails with 401 Unauthorized, try to refresh the token
  if (response.status === 401) {
    try {
      const newAccessToken = await refreshAccessToken();

      // Retry the original request with the new token
      response = await fetch(endpoint, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
          'Authorization': `Bearer ${newAccessToken}`
        }
      });
    } catch (refreshError) {
      // If token refresh fails, user is already redirected in refreshAccessToken
      // Just throw the error to stop execution
      throw refreshError;
    }
  }

  return response;
};

// Chat API Functions
export const fetchPublicMessages = async (limit = 50) => {
  const response = await fetch(`${API_BASE_URL}/chat/public/messages?limit=${limit}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    }
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const postPublicMessage = async (content, guestName) => {
  const response = await fetch(`${API_BASE_URL}/chat/public/messages`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      content,
      guest_name: guestName
    })
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const fetchChatContacts = async () => {
  const response = await apiRequest(`${API_BASE_URL}/chat/contacts`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const fetchPrivateMessages = async (contactId, limit = 50) => {
  const response = await apiRequest(`${API_BASE_URL}/chat/private/messages/${contactId}?limit=${limit}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const postPrivateMessage = async (contactId, content) => {
  const response = await apiRequest(`${API_BASE_URL}/chat/private/messages/${contactId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ content })
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// Diary API Functions
export const fetchDiaryEntry = async (topic, noteType, exerciseType = null) => {
  const params = new URLSearchParams({ topic, note_type: noteType });
  if (exerciseType) {
    params.append('exercise_type', exerciseType);
  }

  const response = await apiRequest(`${API_BASE_URL}/diary/entries?${params.toString()}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const saveDiaryEntry = async ({ topic, noteType, exerciseType = null, note, questionnaire = null }) => {
  const response = await apiRequest(`${API_BASE_URL}/diary/entries`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      topic,
      note_type: noteType,
      exercise_type: exerciseType,
      note,
      questionnaire
    })
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// Function to fetch audio with authentication
export const fetchAudio = async (questionText, speaker = "am_michael") => {
  const audioUrl = `${API_BASE_URL}/audio/speaking`;

  const response = await apiRequest(audioUrl, {
    method: 'POST',
    body: JSON.stringify({
        text: questionText,
        speaker: speaker
    }),
  });

  if (!response.ok) {
    throw new Error(`Audio fetch failed with status: ${response.status}`);
  }

  return response.blob();
};

/**
 * Fetch audio with word-level timing data for karaoke highlighting.
 * @param {string} questionText - Text to convert to speech
 * @param {string} speaker - Voice ID (default: am_michael)
 * @returns {Promise<{audioBlob: Blob, wordTimings: Array, duration: number}>}
 */
export const fetchAudioWithTimings = async (questionText, speaker = "am_michael") => {
  const audioUrl = `${API_BASE_URL}/audio/speaking-with-timings`;

  const response = await apiRequest(audioUrl, {
    method: 'POST',
    body: JSON.stringify({
      text: questionText,
      speaker: speaker
    }),
  });

  if (!response.ok) {
    throw new Error(`Audio fetch with timings failed with status: ${response.status}`);
  }

  const data = await response.json();

  // Convert base64 audio to blob
  const binaryString = atob(data.audio_data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  const audioBlob = new Blob([bytes], { type: 'audio/wav' });

  return {
    audioBlob,
    wordTimings: data.word_timings,
    duration: data.duration
  };
};

export const fetchConversationExercise = async (params) => {
  const { topic } = params
  const query_params = new URLSearchParams({
    topic
  });

  const response = await apiRequest(`${API_BASE_URL}/exercises/conversation?${query_params}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();

  // Transform API response to match expected format
  return {
    dialogue: data.dialogue || [],
    questions: (data.question_answers || []).map(qa => ({
      id: qa.question_number,
      question: qa.question,
      answer: qa.answer,
      alternative_answers: qa.alternative_answers || []
    }))
  };
};

export const fetchConversationFillInExercise = async (params) => {
  const { topic } = params;
  const query_params = new URLSearchParams({
    topic
  });

  const response = await apiRequest(`${API_BASE_URL}/exercises/conversation-fillin?${query_params}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();

  // Transform API response to match expected format
  return {
    dialogue: data.dialogue || [],
    questions: (data.questions || []).map(q => ({
      id: q.id,
      question: q.question,
      partial_answer: q.partial_answer,
      answer: q.answer,
      full_answer: q.full_answer
    })),
    word_options: data.word_options || []
  };
};

export const fetchVocabularyFillInExercise = async (params) => {
  const { topic } = params;
  const query_params = new URLSearchParams({
    topic
  });

  const response = await apiRequest(`${API_BASE_URL}/exercises/vocabulary-fillin?${query_params}`, {
    method: 'GET',
  });


  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  
  // Transform API response to match expected format
  return {
    exercises: (data.exercises || []).map(exercise => ({
      id: exercise.id,
      sentence_before: exercise.sentence_before,
      sentence_after: exercise.sentence_after,
      hint: exercise.hint,
      correct_answer: exercise.correct_answer,
      options: exercise.options
    }))
  };
};

export const fetchGrammarExercise = async (params) => {
  const { topic = 'verb_to_be' } = params;
  const query_params = new URLSearchParams({
    topic,
  });

  const response = await apiRequest(`${API_BASE_URL}/exercises/grammar-fillin?${query_params}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();

  // Transform API response to match expected format
  return {
    exercises: (data.exercises || []).map(exercise => ({
      id: exercise.id,
      sentence: exercise.sentence,
      correct_answer: exercise.correct_answer,
      blank_position: exercise.blank_position || 1,
      examples: exercise.examples || [],
      czech_sentence: exercise.czech_sentence || null,
      svo_analysis: exercise.svo_analysis || null,
      grammar_note: exercise.grammar_note || null,
    })),
    word_options: data.word_options || []
  };
};

export const fetchSpeakingExercise = async (params) => {
  const { topic } = params;
  const query_params = new URLSearchParams({
    topic
  });

  const response = await apiRequest(`${API_BASE_URL}/exercises/speaking?${query_params}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();

  // Transform API response to match expected format
  return {
    instruction: data.instruction || '',
    questions: (data.questions || []).map(q => ({
      id: q.id,
      question: q.question,
      hint: q.hint
    }))
  };
};

export const evaluateConversationWritingExercise = async (exerciseContext, question, userAnswer) => {
  const response = await apiRequest(`${API_BASE_URL}/exercises/evaluate-conversation-writing`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      exercise: exerciseContext,
      question_id: question.id,
      question: question.question,
      user_answer: userAnswer,
      correct_answer: question.alternative_answers?.length > 0
        ? `${question.answer} (Další možné odpovědi: ${question.alternative_answers.join('; ')})`
        : question.answer
    })
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();

  return {
    question_id: data.question_id,
    evaluation: data.evaluation,
    is_correct: data.is_correct
  };
};

// Progress Tracking API Functions

export const recordExerciseAttempt = async (attemptData) => {
  const response = await apiRequest(`${API_BASE_URL}/progress/attempt`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(attemptData)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const getTimeStatistics = async () => {
  const response = await apiRequest(`${API_BASE_URL}/progress/time-stats`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const getProgressOverview = async () => {
  const response = await apiRequest(`${API_BASE_URL}/progress/overview`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// Exercise Progress API Functions

export const saveExerciseProgress = async (topic, exerciseType, progressData) => {
  const response = await apiRequest(`${API_BASE_URL}/progress/save-exercise`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      topic,
      exercise_type: exerciseType,
      progress_data: progressData
    })
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const loadExerciseProgress = async (topic, exerciseType) => {
  const response = await apiRequest(`${API_BASE_URL}/progress/exercise/${encodeURIComponent(topic)}/${encodeURIComponent(exerciseType)}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const resetExerciseProgress = async (topic, exerciseType) => {
  const response = await apiRequest(`${API_BASE_URL}/progress/exercise/${encodeURIComponent(topic)}/${encodeURIComponent(exerciseType)}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const getAllTopicsExercisesStatus = async () => {
  const response = await apiRequest(`${API_BASE_URL}/progress/topics/exercises-status`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// Admin Exercise Update API Functions

export const updateVocabularyFillinExercise = async (exerciseId, updateData, topic) => {
  const url = `${API_BASE_URL}/exercises/update/vocabulary-fillin/${exerciseId}`;
  const params = topic ? `?topic=${encodeURIComponent(topic)}` : '';

  const response = await apiRequest(url + params, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updateData)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const updateGrammarFillinExercise = async (exerciseId, updateData, topic) => {
  const url = `${API_BASE_URL}/exercises/update/grammar-fillin/${exerciseId}`;
  const params = topic ? `?topic=${encodeURIComponent(topic)}` : '';

  const response = await apiRequest(url + params, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updateData)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const updateSpeakingExercise = async (exerciseId, updateData, topic) => {
  const url = `${API_BASE_URL}/exercises/update/speaking/${exerciseId}`;
  const params = topic ? `?topic=${encodeURIComponent(topic)}` : '';

  const response = await apiRequest(url + params, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updateData)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// Fetch all exercises for admin management
export const fetchAllVocabularyFillinExercises = async (topic = null) => {
  const url = `${API_BASE_URL}/admin/exercises/vocabulary-fillin`;
  const params = topic ? `?topic=${encodeURIComponent(topic)}` : '';

  const response = await apiRequest(url + params, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const fetchAllGrammarFillinExercises = async (topic = null) => {
  const url = `${API_BASE_URL}/admin/exercises/grammar-fillin`;
  const params = topic ? `?topic=${encodeURIComponent(topic)}` : '';

  const response = await apiRequest(url + params, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const fetchAllSpeakingExercises = async (topic = null) => {
  const url = `${API_BASE_URL}/admin/exercises/speaking`;
  const params = topic ? `?topic=${encodeURIComponent(topic)}` : '';

  const response = await apiRequest(url + params, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const fetchAdminStats = async () => {
  const url = `${API_BASE_URL}/admin/stats`;

  const response = await apiRequest(url, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const fetchDAUStats = async (days = 7) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/analytics/dau?days=${days}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const fetchAdminDiaryEntries = async () => {
  const response = await apiRequest(`${API_BASE_URL}/admin/diary-entries`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// Create exercise functions
export const createVocabularyFillinExercise = async (data) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/exercises/vocabulary-fillin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const createGrammarFillinExercise = async (data) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/exercises/grammar-fillin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const createSpeakingExercise = async (data) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/exercises/speaking`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// Delete exercise functions
export const deleteVocabularyFillinExercise = async (exerciseId) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/exercises/vocabulary-fillin/${exerciseId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const deleteGrammarFillinExercise = async (exerciseId) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/exercises/grammar-fillin/${exerciseId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const deleteSpeakingExercise = async (exerciseId) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/exercises/speaking/${exerciseId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// Conversation CRUD functions
export const fetchAllConversations = async (topic = null) => {
  const url = `${API_BASE_URL}/admin/exercises/conversations`;
  const params = topic ? `?topic=${encodeURIComponent(topic)}` : '';

  const response = await apiRequest(url + params, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const createConversation = async (data) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/exercises/conversations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const updateConversation = async (conversationId, updateData, topic) => {
  const url = `${API_BASE_URL}/exercises/update/conversation/${conversationId}`;
  const params = topic ? `?topic=${encodeURIComponent(topic)}` : '';

  const response = await apiRequest(url + params, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updateData)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const deleteConversation = async (conversationId) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/exercises/conversations/${conversationId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const updateConversationQA = async (qaId, updateData) => {
  const url = `${API_BASE_URL}/exercises/update/conversation-qa/${qaId}`;

  const response = await apiRequest(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updateData)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// Vocabulary Dictionary API Functions

export const fetchDictionaryWords = async () => {
  const response = await apiRequest(`${API_BASE_URL}/dictionary/words`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const fetchDictionaryWordAudio = async (word) => {
  const response = await apiRequest(`${API_BASE_URL}/dictionary/audio/${encodeURIComponent(word)}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.blob();
};

// User Vocabulary API Functions

export const fetchUserVocabulary = async (topic = null) => {
  const url = `${API_BASE_URL}/user-vocabulary/words`;
  const params = topic ? `?topic=${encodeURIComponent(topic)}` : '';

  const response = await apiRequest(url + params, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const addUserVocabularyWord = async (data) => {
  const response = await apiRequest(`${API_BASE_URL}/user-vocabulary/words`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const updateUserVocabularyWord = async (id, data) => {
  const response = await apiRequest(`${API_BASE_URL}/user-vocabulary/words/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const deleteUserVocabularyWord = async (id) => {
  const response = await apiRequest(`${API_BASE_URL}/user-vocabulary/words/${id}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
};

export const generateAllDictionaryAudio = async () => {
  const response = await apiRequest(`${API_BASE_URL}/admin/vocabulary-dictionary/generate-audio`, {
    method: 'POST',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const generateAllSpeakingAudio = async () => {
  const response = await apiRequest(`${API_BASE_URL}/admin/speaking-exercises/generate-audio`, {
    method: 'POST',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const generateAllConversationAudio = async () => {
  const response = await apiRequest(`${API_BASE_URL}/admin/conversations/generate-audio`, {
    method: 'POST',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// Writing Exercise API Functions

export const fetchWritingExercise = async (params) => {
  const { topic } = params;
  const query_params = new URLSearchParams({
    topic
  });

  const response = await apiRequest(`${API_BASE_URL}/exercises/writing?${query_params}`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();

  return {
    id: data.id,
    title: data.title,
    instruction: data.instruction,
    min_sentences: data.min_sentences,
    guiding_questions: data.guiding_questions || [],
    examples: data.examples || []
  };
};

export const evaluateWritingSentence = async (data) => {
  const response = await apiRequest(`${API_BASE_URL}/exercises/evaluate-writing-sentence`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// BBB
export const joinMeeting = async (meetingId, userName, role) => {
  const response = await apiRequest(`${API_BASE_URL}/bbb/join`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      meeting_id: meetingId,
      user_name: userName,
      role
    })
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to join meeting');
  }

  const data = await response.json();
  return data.joinUrl;

};

export const translateWord = async (word) => {
  const response = await apiRequest(`${API_BASE_URL}/translate/word`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ word }),
  });

  if (!response.ok) {
    throw new Error(`Translation failed: ${response.status}`);
  }

  return await response.json();
};

export const fetchGlobalDictionary = async () => {
  const response = await apiRequest(`${API_BASE_URL}/admin/global-dictionary`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch global dictionary: ${response.status}`);
  }

  return await response.json();
};

export const createGlobalDictionaryEntry = async (data) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/global-dictionary`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to create entry: ${response.status}`);
  }

  return await response.json();
};

export const updateGlobalDictionaryEntry = async (entryId, data) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/global-dictionary/${entryId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to update entry: ${response.status}`);
  }

  return await response.json();
};

export const deleteGlobalDictionaryEntry = async (entryId) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/global-dictionary/${entryId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`Failed to delete entry: ${response.status}`);
  }

  return await response.json();
};

export const fetchAdminUsers = async () => {
  const response = await apiRequest(`${API_BASE_URL}/auth/users`);
  if (!response.ok) {
    throw new Error(`Failed to fetch users: ${response.status}`);
  }
  return await response.json();
};

export const updateAdminUser = async (userId, data) => {
  const response = await apiRequest(`${API_BASE_URL}/auth/users/${userId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error(`Failed to update user: ${response.status}`);
  }
  return await response.json();
};

export const fetchPurgePreview = async () => {
  const response = await apiRequest(`${API_BASE_URL}/admin/purge-exercises/preview`, {
    method: 'GET',
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return await response.json();
};

export const purgeExercises = async (exerciseTypes, purgeUserProgress = false) => {
  const response = await apiRequest(`${API_BASE_URL}/admin/purge-exercises`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      exercise_types: exerciseTypes,
      purge_user_progress: purgeUserProgress,
    }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `Purge failed: ${response.status}`);
  }
  return await response.json();
};
